import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@repo/shared";

// Local translations
export const T = {
  en_GB: {
    // Page
    settings: "Settings",
    users: "Users",
    pageDescription: "Manage users in your organization",
    documentation: "Documentation",
    // Buttons
    addUser: "Add User",
    cancel: "Cancel",
    save: "Save",
    disable: "Disable",
    // Table
    usersCount: "Users",
    email: "Email",
    name: "Name",
    auth: "Auth",
    twoFa: "2FA",
    permissions: "Permissions",
    lastActive: "Last Active",
    actions: "Actions",
    sso: "SSO",
    password: "Password",
    enabled: "Enabled",
    // Search
    searchPlaceholder: "Search users...",
    // Empty state
    noUsersYet: "No users yet.",
    addUsersToGetStarted: "Add users to get started.",
    noSearchResults: "No users match your search.",
    // Delete
    deleteUser: "Delete User",
    deleteUserConfirmation: (email: string) =>
      `Delete "${email}"? They can no longer sign in and their sessions end. Their history stays and shows them as deleted. Creating a user with the same email later — or that person signing in with single sign-on — restores them. Move this user's clients first if any are private.`,
    lastPasswordAdmin:
      "This is the last administrator who can sign in without single sign-on. Give another user with a password the admin permission first.",
    cannotDeleteSelf: "You cannot delete your own user.",
    userRestored:
      "A previously deleted user with this email was restored, including their history.",
    delete: "Delete",
    moveClients: "Move clients",
    error: "Error",
    failedToDeleteUser: "Failed to delete user",
    ok: "OK",
    // Disable 2FA
    disableTwoFactor: "Disable Two-Factor Authentication",
    disableTwoFactorConfirmation: (email: string) =>
      `Are you sure you want to disable 2FA for "${email}"? They will need to set it up again if they want to use it.`,
    disable2FA: "Disable 2FA",
    failedToDisable2FA: "Failed to disable 2FA",
    // SSO Info
    ssoEnabled: "SSO is enabled",
    ssoEnabledDescription:
      "Users who sign in via SSO are created automatically. You can also create users with password authentication for accounts that don't use SSO.",
    // Dialog
    editSsoUserPermissions: "Edit SSO User Permissions",
    editUser: "Edit User",
    addNewUser: "Add New User",
    editSsoUserDescription:
      "Edit permissions for this SSO user. Email, name, and password are managed by the identity provider.",
    editUserDescription: "Update the user information below.",
    addNewUserDescription: "Fill in the information to create a new user.",
    ssoUser: "SSO User",
    managedByIdp: "Managed by identity provider",
    // Form fields
    emailRequired: "Email",
    emailPlaceholder: "user@example.com",
    nameLabel: "Name",
    namePlaceholder: "John Doe",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter password",
    leaveBlankToKeepPassword: "Leave blank to keep current password",
    leaveBlankPasswordPlaceholder: "Leave blank to keep current password",
    passwordMinHint: `Minimum ${PASSWORD_MIN_LENGTH} characters`,
    passwordMinLength: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    passwordMaxLength: `Password must not exceed ${PASSWORD_MAX_LENGTH} characters`,
    // Permissions
    permissionsLabel: "Permissions",
    adminPermission: "Admin - Manage users",
    advisorPermission: "Advisor - Access to clients and flows",
    compliancePermission: "Compliance - Access compliance checks and audits",
    designerPermission: "Designer - Manage flows, risk models, portfolios, PDF templates",
    developerPermission: "Developer - Manage API keys and webhooks",
    permissionsHint: "Users must have at least one permission. Advisor is the default.",
    permissionsSsoHint:
      "Note: Permissions set here may be overwritten on next SSO login if the identity provider syncs roles.",
    // Errors
    somethingWentWrong: "Something went wrong",
    failedToSaveUser: "Failed to save user",
  },
  nb_NO: {
    // Page
    settings: "Innstillinger",
    users: "Brukere",
    pageDescription: "Administrer brukere i organisasjonen din",
    documentation: "Dokumentasjon",
    // Buttons
    addUser: "Legg til bruker",
    cancel: "Avbryt",
    save: "Lagre",
    disable: "Deaktiver",
    // Table
    usersCount: "Brukere",
    email: "E-post",
    name: "Navn",
    auth: "Autentisering",
    twoFa: "2FA",
    permissions: "Tillatelser",
    lastActive: "Sist aktiv",
    actions: "Handlinger",
    sso: "SSO",
    password: "Passord",
    enabled: "Aktivert",
    // Search
    searchPlaceholder: "Søk etter brukere...",
    // Empty state
    noUsersYet: "Ingen brukere ennå.",
    addUsersToGetStarted: "Legg til brukere for å komme i gang.",
    noSearchResults: "Ingen brukere samsvarer med søket ditt.",
    // Delete
    deleteUser: "Slett bruker",
    deleteUserConfirmation: (email: string) =>
      `Slette «${email}»? Brukeren kan ikke lenger logge inn, og aktive økter avsluttes. Historikken beholdes og viser brukeren som slettet. Hvis du senere oppretter en bruker med samme e-post – eller personen logger inn med single sign-on – gjenopprettes brukeren. Flytt brukerens kunder først hvis noen er private.`,
    lastPasswordAdmin:
      "Dette er den siste administratoren som kan logge inn uten single sign-on. Gi en annen bruker med passord administratortilgang først.",
    cannotDeleteSelf: "Du kan ikke slette din egen bruker.",
    userRestored:
      "En tidligere slettet bruker med denne e-posten ble gjenopprettet, inkludert historikken.",
    delete: "Slett",
    moveClients: "Flytt kunder",
    error: "Feil",
    failedToDeleteUser: "Kunne ikke slette bruker",
    ok: "OK",
    // Disable 2FA
    disableTwoFactor: "Deaktiver tofaktorautentisering",
    disableTwoFactorConfirmation: (email: string) =>
      `Er du sikker på at du vil deaktivere 2FA for "${email}"? De må sette det opp på nytt hvis de vil bruke det.`,
    disable2FA: "Deaktiver 2FA",
    failedToDisable2FA: "Kunne ikke deaktivere 2FA",
    // SSO Info
    ssoEnabled: "SSO er aktivert",
    ssoEnabledDescription:
      "Brukere som logger inn via SSO opprettes automatisk. Du kan også opprette brukere med passordautentisering for kontoer som ikke bruker SSO.",
    // Dialog
    editSsoUserPermissions: "Rediger SSO-brukertillatelser",
    editUser: "Rediger bruker",
    addNewUser: "Legg til ny bruker",
    editSsoUserDescription:
      "Rediger tillatelser for denne SSO-brukeren. E-post, navn og passord administreres av identitetsleverandøren.",
    editUserDescription: "Oppdater brukerinformasjonen nedenfor.",
    addNewUserDescription: "Fyll inn informasjonen for å opprette en ny bruker.",
    ssoUser: "SSO-bruker",
    managedByIdp: "Administreres av identitetsleverandør",
    // Form fields
    emailRequired: "E-post",
    emailPlaceholder: "bruker@eksempel.com",
    nameLabel: "Navn",
    namePlaceholder: "Ola Nordmann",
    passwordLabel: "Passord",
    passwordPlaceholder: "Skriv inn passord",
    leaveBlankToKeepPassword: "La stå tom for å beholde nåværende passord",
    leaveBlankPasswordPlaceholder: "La stå tom for å beholde nåværende passord",
    passwordMinHint: `Minimum ${PASSWORD_MIN_LENGTH} tegn`,
    passwordMinLength: `Passordet må være minst ${PASSWORD_MIN_LENGTH} tegn`,
    passwordMaxLength: `Passordet kan ikke overstige ${PASSWORD_MAX_LENGTH} tegn`,
    // Permissions
    permissionsLabel: "Tillatelser",
    adminPermission: "Administrator - Administrer brukere",
    advisorPermission: "Rådgiver - Tilgang til kunder og flyter",
    compliancePermission: "Compliance - Tilgang til samsvarskontroller og revisjoner",
    designerPermission: "Designer - Administrer flyter, risikomodeller, porteføljer, PDF-maler",
    developerPermission: "Utvikler - Administrer API-nøkler og webhooks",
    permissionsHint: "Brukere må ha minst én tillatelse. Rådgiver er standard.",
    permissionsSsoHint:
      "Merk: Tillatelser satt her kan bli overskrevet ved neste SSO-innlogging hvis identitetsleverandøren synkroniserer roller.",
    // Errors
    somethingWentWrong: "Noe gikk galt",
    failedToSaveUser: "Kunne ikke lagre bruker",
  },
} as const;
